import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  MessageSquare, Send, Plus, Trash2, Loader2, FileText,
  Clock, Bot, User as UserIcon, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Markdown } from "@/components/Markdown";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div className={cn("flex gap-3 mb-4", isUser ? "flex-row-reverse" : "")} data-testid={`msg-${message.role}`}>
      <div className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
        isUser ? "bg-blue-600" : "bg-slate-100"
      )}>
        {isUser
          ? <UserIcon className="w-4 h-4 text-white" />
          : <Bot className="w-4 h-4 text-blue-600" />
        }
      </div>
      <div className={cn(
        "max-w-[75%] rounded-xl px-4 py-3 text-sm leading-relaxed",
        isUser
          ? "bg-blue-600 text-white"
          : "bg-slate-50 border border-slate-200 text-slate-800"
      )}>
        {isUser
          ? <div className="whitespace-pre-wrap">{message.content}</div>
          : <Markdown content={message.content} />
        }
      </div>
    </div>
  );
}

function SessionList({ sessions, activeId, onSelect, onDelete, onNew }) {
  return (
    <div className="space-y-1">
      <Button variant="outline" size="sm" className="w-full justify-start gap-2 mb-3" onClick={onNew} data-testid="new-chat-btn">
        <Plus className="w-4 h-4" />
        Nouvelle conversation
      </Button>
      {sessions.map(function(s) {
        return (
          <div
            key={s.session_id}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer group transition-colors",
              activeId === s.session_id ? "bg-blue-50 text-blue-700" : "hover:bg-slate-50"
            )}
            onClick={function() { onSelect(s.session_id); }}
            data-testid={"session-" + s.session_id}
          >
            <MessageSquare className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm truncate flex-1">{s.title}</span>
            <button
              className="opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={function(e) { e.stopPropagation(); onDelete(s.session_id); }}
              data-testid={"delete-session-" + s.session_id}
            >
              <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-destructive" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default function AIChat({ user, setUser }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(function() { fetchSessions(); }, []);
  useEffect(function() { scrollToBottom(); }, [messages]);

  function scrollToBottom() {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }

  async function fetchSessions() {
    try {
      const res = await fetch(API_URL + "/api/ai/chat/sessions", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
      }
    } catch (e) { /* ignore */ }
    setLoadingSessions(false);
  }

  async function loadSession(sessionId) {
    setActiveSessionId(sessionId);
    try {
      const res = await fetch(API_URL + "/api/ai/chat/sessions/" + sessionId, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (e) { toast.error("Erreur de chargement"); }
  }

  function startNewChat() {
    setActiveSessionId(null);
    setMessages([]);
    setInput("");
  }

  async function deleteSession(sessionId) {
    try {
      await fetch(API_URL + "/api/ai/chat/sessions/" + sessionId, { method: "DELETE", credentials: "include" });
      setSessions(function(prev) { return prev.filter(function(s) { return s.session_id !== sessionId; }); });
      if (activeSessionId === sessionId) startNewChat();
      toast.success("Conversation supprimée");
    } catch (e) { toast.error("Erreur"); }
  }

  async function handleSend() {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput("");
    setSending(true);

    setMessages(function(prev) {
      return prev.concat([{ role: "user", content: userMsg, timestamp: new Date().toISOString() }]);
    });

    try {
      const res = await fetch(API_URL + "/api/ai/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: userMsg, session_id: activeSessionId })
      });

      if (res.ok) {
        const data = await res.json();
        if (!activeSessionId) {
          setActiveSessionId(data.session_id);
          fetchSessions();
        }
        setMessages(function(prev) {
          return prev.concat([{ role: "assistant", content: data.response, timestamp: new Date().toISOString() }]);
        });
      } else {
        toast.error("Erreur de l'assistant IA");
      }
    } catch (e) {
      toast.error("Erreur de connexion");
    }
    setSending(false);
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const suggestions = [
    "Génère un contrat de travail CDI avec les clauses standards",
    "Rédige une clause de non-concurrence équilibrée",
    "Crée un contrat de prestation de services informatiques",
    "Propose un modèle de statuts SAS pour 3 associés"
  ];

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="flex h-[calc(100vh-0px)]" data-testid="ai-chat-page">
        {/* Sessions sidebar */}
        <div className="w-72 border-r border-slate-200 bg-white p-4 hidden lg:block">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-blue-600" />
            <h2 className="font-semibold text-sm">Assistant GPT-5.2</h2>
          </div>
          <ScrollArea className="h-[calc(100vh-120px)]">
            <SessionList
              sessions={sessions}
              activeId={activeSessionId}
              onSelect={loadSession}
              onDelete={deleteSession}
              onNew={startNewChat}
            />
          </ScrollArea>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col bg-white">
          {/* Header */}
          <div className="h-14 border-b border-slate-200 flex items-center px-6 justify-between">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-blue-600" />
              <h1 className="font-semibold text-base">
                {activeSessionId ? "Conversation en cours" : "Nouvelle conversation"}
              </h1>
            </div>
            <Badge variant="secondary" className="text-xs">GPT-5.2</Badge>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-6">
            {messages.length === 0 && !sending ? (
              <div className="flex flex-col items-center justify-center h-full py-16">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6">
                  <MessageSquare className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="font-heading text-xl font-semibold mb-2">Assistant Juridique IA</h2>
                <p className="text-sm text-muted-foreground mb-8 text-center max-w-md">
                  Posez vos questions juridiques, demandez la génération de contrats ou obtenez des suggestions de clauses.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl w-full">
                  {suggestions.map(function(s, i) {
                    return (
                      <button
                        key={i}
                        className="text-left p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors text-sm text-slate-600"
                        onClick={function() { setInput(s); }}
                        data-testid={"suggestion-" + i}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto">
                {messages.map(function(msg, i) {
                  return <MessageBubble key={i} message={msg} />;
                })}
                {sending && (
                  <div className="flex gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="border-t border-slate-200 p-4">
            <div className="max-w-3xl mx-auto flex gap-3">
              <Textarea
                value={input}
                onChange={function(e) { setInput(e.target.value); }}
                onKeyDown={handleKeyDown}
                placeholder="Posez votre question juridique..."
                className="min-h-[48px] max-h-[120px] resize-none"
                rows={1}
                data-testid="chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || sending}
                className="h-12 w-12 p-0"
                data-testid="send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">
              GPT-5.2 peut faire des erreurs. Vérifiez les informations juridiques importantes.
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
